/**
 * Сервис для отправки heartbeat на Backend
 * Работает независимо от NetworkManager для максимальной надежности
 */
import { apiClient } from './apiClient'
import { useDeviceStore } from '../store/deviceStore'
import { EventsRepo } from '../offline/eventsRepo'
import { LogsRepo } from '../offline/logsRepo'
import { QueueProcessor } from '../offline/queueProcessor'
import { db } from '../offline/db'
import { NetworkMonitor } from './networkMonitor'

export interface HeartbeatData {
  device_id: string
  site_id: string
  version: string
  network_type?: 'wifi' | 'mobile' | 'unknown'
  network_quality?: 'poor' | 'normal' | 'good'
  network_quality_detailed?: {
    type: string
    bars: number
    downlink_mbps: number
    rtt_ms: number
    effectiveType: string
  }
  local_db_size_bytes?: number
  unsynced_events_count: number
}

export interface HeartbeatResponse {
  success: boolean
  data: {
    commands: Array<{
      id: string
      command_type: string
      payload: Record<string, unknown>
    }>
    expected_version?: string
  }
}

export class HeartbeatService {
  private static versionMismatchCount = 0
  // После N consecutive mismatch-heartbeat'ов — форс-reload (последний резерв)
  private static readonly MAX_MISMATCHES_BEFORE_FORCE = 6 // 6 × 30s = 3 минуты
  private static readonly FORCE_RELOAD_STORAGE_KEY = 'last_force_reload_timestamp'
  private static readonly FORCE_RELOAD_COOLDOWN_MS = 5 * 60 * 1000 // 5 минут
  
  /**
   * Отправляет heartbeat на сервер
   * Глобальная защита от ошибок - heartbeat должен работать всегда, даже при ошибках в приложении
   */
  static async sendHeartbeat(): Promise<HeartbeatResponse | null> {
    try {
      const deviceStore = useDeviceStore.getState()
      
      if (!deviceStore.deviceId || !deviceStore.siteId) {
        await LogsRepo.save('warn', 'Cannot send heartbeat: device not registered')
        return null
      }

      // КРИТИЧНО: НЕ проверяем NetworkManager.isOnline() - он может ложно определить offline
      // Вместо этого всегда пытаемся отправить heartbeat
      // Реальный offline определится через network error в catch блоке ниже
      // Это обеспечивает доставку команд даже при проблемах с NetworkManager

      // Получаем количество несинхронизированных событий
      const unsyncedCount = await EventsRepo.getUnsyncedCount()

    // Оцениваем размер IndexedDB
    const dbSize = await this.estimateDbSize()

    // Измеряем качество сети (RTT)
    const networkQuality = await this.measureNetworkQuality()
    const networkType = this.detectNetworkType()
    
    // Получаем детальную информацию о качестве сети
    const networkQualityDetailed = NetworkMonitor.getQuality()

    // Не отправляем heartbeat для тестовых устройств
    if (!deviceStore.deviceId || deviceStore.deviceId.startsWith('test-') || 
        !deviceStore.siteId || deviceStore.siteId.startsWith('test-')) {
      return null
    }

    const heartbeatData: HeartbeatData = {
      device_id: deviceStore.deviceId,
      site_id: deviceStore.siteId,
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      network_type: networkType,
      network_quality: networkQuality,
      network_quality_detailed: {
        type: networkQualityDetailed.type,
        bars: networkQualityDetailed.bars,
        downlink_mbps: networkQualityDetailed.downlink,
        rtt_ms: networkQualityDetailed.rtt,
        effectiveType: networkQualityDetailed.effectiveType
      },
      local_db_size_bytes: dbSize,
      unsynced_events_count: unsyncedCount,
    }

    try {
      const response = await apiClient.post<HeartbeatResponse>(
        '/tablet/heartbeat',
        heartbeatData
      )

      await LogsRepo.save('info', `Heartbeat sent, unsynced: ${unsyncedCount}`)

      // Проверяем версию приложения (автоматическое обновление)
      const expectedVersion = response.data?.expected_version
      const currentVersion = import.meta.env.VITE_APP_VERSION
      
      if (expectedVersion && currentVersion && expectedVersion !== currentVersion) {
        this.versionMismatchCount++
        console.log(`[Heartbeat] 🔄 Version mismatch (${this.versionMismatchCount}/${this.MAX_MISMATCHES_BEFORE_FORCE}): current=${currentVersion}, expected=${expectedVersion}`)

        // Триггерим проверку SW и отправляем SKIP_WAITING если есть waiting SW.
        // Фактический reload произойдёт через controllerchange слушатель в main.tsx.
        // Только триггерим проверку обновления SW.
        // vite-plugin-pwa с autoUpdate сам обнаружит новый waiting SW,
        // отправит SKIP_WAITING и сделает reload через свой controllerchange.
        // Мы НЕ делаем reload сами — иначе два механизма конфликтуют → бесконечный цикл.
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.getRegistration()
            if (registration) {
              await registration.update()
              console.log('[Heartbeat] 🔄 SW update check triggered')
            }
          } catch (err) {
            console.error('[Heartbeat] SW update check failed:', err)
          }
        }

        // Последний резерв: если версия не совпадает слишком долго — форс-reload.
        // Это покрывает случай когда SW вообще не обновился (нет нового SW на сервере).
        if (this.versionMismatchCount >= this.MAX_MISMATCHES_BEFORE_FORCE) {
          const lastForce = localStorage.getItem(this.FORCE_RELOAD_STORAGE_KEY)
          const now = Date.now()
          const cooldownOk = !lastForce || (now - parseInt(lastForce)) > this.FORCE_RELOAD_COOLDOWN_MS

          if (cooldownOk) {
            console.log('[Heartbeat] 🔥 Force reload after persistent mismatch...')
            localStorage.setItem(this.FORCE_RELOAD_STORAGE_KEY, now.toString())
            this.versionMismatchCount = 0
            window.location.reload()
          } else {
            this.versionMismatchCount = 0 // Сбросим чтобы не спамить
          }
        }
      } else if (expectedVersion && currentVersion && expectedVersion === currentVersion) {
        if (this.versionMismatchCount > 0) {
          console.log('[Heartbeat] ✅ Version match! Resetting counter')
          this.versionMismatchCount = 0
          localStorage.removeItem(this.FORCE_RELOAD_STORAGE_KEY)
        }
      }

      return response
    } catch (error) {
      // Проверяем статус ошибки
      const errorMessage = error instanceof Error ? error.message : String(error)
      
      // Если устройство удалено или неавторизовано (401/404)
      if (errorMessage.includes('401') || errorMessage.includes('404') || 
          errorMessage.includes('Unauthorized') || errorMessage.includes('Not Found')) {
        console.error('[HeartbeatService] Device deleted or unauthorized, setting flag')
        localStorage.setItem('device_deleted', 'true')
        await LogsRepo.save('error', 'Device deleted from system')
        // Перезагружаем страницу чтобы показать экран удаления
        window.location.reload()
        return null
      }
      
      // Проверяем, является ли ошибка сетевой
      const isNetworkError = error instanceof Error && 
        (error.name === 'NetworkError' || 
         error.message.includes('Failed to fetch') ||
         error.message.includes('интернету'))

      // Если сетевая ошибка, НЕ логируем
      // NetworkManager уже показывает индикатор офлайна
      if (!isNetworkError) {
        await LogsRepo.save('error', `Heartbeat failed: ${errorMessage}`)
      }
      
      return null
    }
    } catch (criticalError) {
      // Глобальная защита - ловим ВСЕ неожиданные ошибки
      // Это гарантирует что heartbeat продолжит работать даже при критических ошибках
      console.error('[HeartbeatService] Critical error in sendHeartbeat:', criticalError)
      
      // Пытаемся залогировать, но защищаем даже логирование
      try {
        const errorMsg = criticalError instanceof Error ? criticalError.message : 'unknown critical error'
        await LogsRepo.save('error', `Critical heartbeat error: ${errorMsg}`)
      } catch (logError) {
        // Даже если логирование упало, не падаем
        console.error('[HeartbeatService] Failed to log critical error:', logError)
      }
      
      return null
    }
  }

  /**
   * Запускает периодическую отправку heartbeat с retry логикой
   * При ошибках автоматически повторяет попытки с экспоненциальной задержкой
   */
  static startPeriodicHeartbeat(intervalMs: number = 60000): () => void {
    let intervalId: number | null = null
    let retryTimeoutId: number | null = null
    let consecutiveFailures = 0
    const MAX_RETRIES = 3

    const send = async () => {
      try {
        const response = await HeartbeatService.sendHeartbeat()
        
        // Успех - сбрасываем счетчик ошибок
        if (response !== null) {
          if (consecutiveFailures > 0) {
            console.log(`[HeartbeatService] ✅ Heartbeat recovered after ${consecutiveFailures} failures`)
          }
          consecutiveFailures = 0
        }
        
        // Обрабатываем команды из ответа (если есть)
        if (response?.data?.commands && response.data.commands.length > 0) {
          console.log(`[HeartbeatService] 📦 Received ${response.data.commands.length} commands from server`)
          await QueueProcessor.processCommands(response.data.commands)
        }
      } catch (error) {
        console.error('[HeartbeatService] Periodic heartbeat error:', error)
        
        // Увеличиваем счетчик последовательных ошибок
        consecutiveFailures++
        
        // Retry с экспоненциальной задержкой (10s, 20s, 40s)
        if (consecutiveFailures <= MAX_RETRIES) {
          // Вычисляем задержку: 10s * 2^(attempt-1), максимум 40s
          const retryDelay = Math.min(10000 * Math.pow(2, consecutiveFailures - 1), 40000)
          console.log(`[HeartbeatService] 🔄 Retry in ${retryDelay / 1000}s (attempt ${consecutiveFailures}/${MAX_RETRIES})`)
          
          // Очищаем предыдущий retry timeout если есть
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId)
          }
          
          // Планируем retry
          retryTimeoutId = window.setTimeout(() => {
            console.log(`[HeartbeatService] 🔄 Retrying heartbeat (attempt ${consecutiveFailures}/${MAX_RETRIES})`)
            send()
          }, retryDelay)
        } else {
          console.error(`[HeartbeatService] ❌ Max retries (${MAX_RETRIES}) reached, waiting for next interval`)
        }
      }
    }

    // Отправляем сразу при старте
    console.log(`[HeartbeatService] 🚀 Starting periodic heartbeat (interval: ${intervalMs / 1000}s)`)
    send()

    // Затем периодически
    intervalId = window.setInterval(() => {
      // Сбрасываем счетчик ошибок при новом цикле
      consecutiveFailures = 0
      send()
    }, intervalMs)

    // Возвращаем функцию для остановки
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
        console.log('[HeartbeatService] 🛑 Stopped periodic heartbeat')
      }
      if (retryTimeoutId !== null) {
        clearTimeout(retryTimeoutId)
        console.log('[HeartbeatService] 🛑 Cancelled retry timeout')
      }
    }
  }

  /**
   * Оценивает размер IndexedDB
   */
  private static async estimateDbSize(): Promise<number> {
    try {
      // Получаем оценку размера через navigator.storage
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate()
        return estimate.usage || 0
      }

      // Альтернативный способ: подсчитываем примерный размер данных
      const eventsCount = await db.events.count()
      const logsCount = await db.logs.count()
      const employeesCount = await db.employees_cache.count()

      // Примерная оценка: ~500 байт на событие, ~200 байт на лог, ~300 байт на ключ
      return eventsCount * 500 + logsCount * 200 + employeesCount * 300
    } catch (error) {
      console.error('Failed to estimate DB size:', error)
      return 0
    }
  }

  /**
   * Измеряет качество сети через RTT
   */
  private static async measureNetworkQuality(): Promise<
    'poor' | 'normal' | 'good'
  > {
    if (!navigator.onLine) {
      return 'poor'
    }

    try {
      const startTime = performance.now()
      
      // Делаем HEAD запрос для измерения RTT
      const apiBaseUrl = (apiClient as any).baseUrl || 'https://dshmelev-kyinrwjl.dev.internal.qrium.ru'
      await fetch(`${apiBaseUrl}/health`, {
        method: 'HEAD',
        signal: AbortSignal.timeout(5000), // Таймаут 5 секунд
      })

      const endTime = performance.now()
      const rtt = endTime - startTime

      // Определяем качество на основе RTT (соответствует NetworkQuality enum: poor, normal, good)
      if (rtt < 100) return 'good'
      if (rtt < 300) return 'normal'
      return 'poor'
    } catch (error) {
      // Если запрос не удался, считаем сеть плохой
      return 'poor'
    }
  }

  /**
   * Определяет тип сети (соответствует NetworkType enum: wifi, mobile, unknown)
   */
  private static detectNetworkType(): 'wifi' | 'mobile' | 'unknown' {
    // В браузере сложно точно определить тип сети
    // Используем Connection API если доступен
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection

    if (connection) {
      const effectiveType = connection.effectiveType
      if (effectiveType === '4g' || effectiveType === '3g' || effectiveType === '2g') {
        return 'mobile'
      }
      if (connection.type === 'wifi') {
        return 'wifi'
      }
    }

    // По умолчанию считаем WiFi для планшетов
    return 'wifi'
  }
}

